import { createContext, useContext, useEffect, useState } from "react";
import { api, tokenStorage, preauthStorage, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

const PIN_IDLE_MS    = 10 * 60 * 1000;   // 10 minutes → re-enter PIN
const LOGOUT_IDLE_MS = 60 * 60 * 1000;   // 1 hour    → full sign-out
const LAST_ACTIVITY_KEY = "d4ps_last_activity";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);     // null = checking
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = tokenStorage.get();

    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        // Always require PIN re-entry on a fresh session (page load / new tab),
        // even if the existing token already carries pin_verified=true.
        setUser({ ...r.data, pin_verified: false });
      })
      .catch(() => {
        tokenStorage.clear();
        setUser(false);
      })
      .finally(() => setReady(true));
  }, []);

  const markActivity = () => {
    try { localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); } catch (_) { /* ignore */ }
  };

  /** Password step. Returns one of:
   *   { status: "signed-in", user } — trusted device / no 2FA needed
   *   { status: "2fa-verify", email } — existing 2FA, prompt for code
   *   { status: "2fa-enroll", email } — first sign-in, must enroll
   */
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.requires_2fa) {
      preauthStorage.set(data.preauth_token);
      tokenStorage.clear();
      return { status: "2fa-verify", email: data.email };
    }
    if (data?.requires_2fa_enrollment) {
      preauthStorage.set(data.preauth_token);
      tokenStorage.clear();
      return { status: "2fa-enroll", email: data.email };
    }
    // Trusted device / bypass path — issue full session
    preauthStorage.clear();
    tokenStorage.set(data.access_token);
    markActivity();
    setUser({
      id: data.id, email: data.email, name: data.name, role: data.role,
      pin_verified: !!data.pin_verified, has_pin: !!data.has_pin,
      has_2fa: !!data.has_2fa,
      permissions: data.permissions ?? null,
    });
    return { status: "signed-in", user: data };
  };

  /** Complete either the enrol/confirm, verify or verify-backup step and
   *  install the returned access token. */
  const _finishTwoFactor = async (path, body) => {
    const preauth = preauthStorage.get();
    if (!preauth) throw new Error("Session expired — please sign in again.");
    const { data } = await api.post(path, body, {
      headers: { Authorization: `Bearer ${preauth}` },
    });
    preauthStorage.clear();
    tokenStorage.set(data.access_token);
    markActivity();
    setUser({
      id: data.id, email: data.email, name: data.name, role: data.role,
      pin_verified: !!data.pin_verified, has_pin: !!data.has_pin,
      has_2fa: !!data.has_2fa,
      permissions: data.permissions ?? null,
    });
    return data;
  };

  const enrollStart = async () => {
    const preauth = preauthStorage.get();
    if (!preauth) throw new Error("Session expired — please sign in again.");
    const { data } = await api.post("/auth/2fa/enroll/start", {}, {
      headers: { Authorization: `Bearer ${preauth}` },
    });
    return data; // { qr_data_url, manual_secret, otpauth_uri, issuer, account }
  };
  const enrollConfirm = (code, trust_device) =>
    _finishTwoFactor("/auth/2fa/enroll/confirm", { code, trust_device });
  const verifyTotp = (code, trust_device) =>
    _finishTwoFactor("/auth/2fa/verify", { code, trust_device });
  const verifyBackup = (code, trust_device) =>
    _finishTwoFactor("/auth/2fa/verify-backup", { code, trust_device });

  const verifyPin = async (pin) => {
    const { data } = await api.post("/auth/verify-pin", { pin });
    tokenStorage.set(data.access_token);
    markActivity();
    setUser({
      id: data.id, email: data.email, name: data.name, role: data.role,
      pin_verified: true, has_pin: true, has_2fa: !!data.has_2fa,
      permissions: data.permissions ?? null,
    });
    return data;
  };

  const setupPin = async (pin) => {
    const { data } = await api.post("/auth/setup-pin", { pin });
    tokenStorage.set(data.access_token);
    markActivity();
    setUser({
      id: data.id, email: data.email, name: data.name, role: data.role,
      pin_verified: true, has_pin: true, has_2fa: !!data.has_2fa,
      permissions: data.permissions ?? null,
    });
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) { /* ignore */ }
    tokenStorage.clear();
    preauthStorage.clear();
    try { localStorage.removeItem(LAST_ACTIVITY_KEY); } catch (_) { /* ignore */ }
    setUser(false);
  };

  // Inactivity tracking — only while signed in
  useEffect(() => {
    if (!user || user === false) return undefined;
    markActivity();
    const handler = () => markActivity();
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));

    const interval = setInterval(() => {
      const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
      const idle = Date.now() - last;
      if (!last) return;
      if (idle >= LOGOUT_IDLE_MS) {
        logout();
      } else if (idle >= PIN_IDLE_MS) {
        // Lock back to PIN screen — keep session token but mark unverified
        setUser((u) => (u && u.pin_verified ? { ...u, pin_verified: false } : u));
      }
    }, 5 * 1000);

    return () => {
      clearInterval(interval);
      events.forEach((e) => window.removeEventListener(e, handler));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user && user.id, user && user.pin_verified]);

  return (
    <AuthContext.Provider
      value={{
        user, ready,
        login, logout,
        verifyPin, setupPin,
        enrollStart, enrollConfirm, verifyTotp, verifyBackup,
        formatApiError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
